import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiHome, FiUpload, FiTrash2 } from 'react-icons/fi';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

const HeroImageManager = () => {
  const [currentHero, setCurrentHero] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHeroImage();
  }, []);

  const loadHeroImage = async () => {
    try {
      const docRef = doc(db, 'shop', 'hero');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const heroUrl = docSnap.data().url;
        setCurrentHero(heroUrl);
        setPreview(heroUrl);
      }
    } catch (error) {
      console.error('Error loading hero image:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (preview) {
      try {
        await setDoc(doc(db, 'shop', 'hero'), { url: preview });
        setCurrentHero(preview);
        alert('Hero image updated successfully!');
      } catch (error) {
        console.error('Error saving hero image:', error);
        alert('Failed to save hero image. Please try again.');
      }
    }
  };

  const handleRemove = async () => {
    if (window.confirm('Are you sure you want to remove the hero image?')) {
      try {
        await setDoc(doc(db, 'shop', 'hero'), { url: null });
        setCurrentHero(null);
        setPreview(null);
      } catch (error) {
        console.error('Error removing hero image:', error);
        alert('Failed to remove hero image. Please try again.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white">Loading hero image...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <FiHome className="text-2xl sm:text-3xl text-white" />
        <h2 className="text-xl sm:text-2xl font-bold text-white">Hero Background Image</h2>
      </div>

      {/* Guidelines */}
      <div className="mb-4 sm:mb-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-3 sm:p-4">
        <p className="text-blue-300 text-xs sm:text-sm">
          <strong>Recommended:</strong> Wide landscape image, 1920x1080px or larger. 
          This image will be used as the background for your homepage hero section.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {/* Upload Section */}
        <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Upload New Hero Image</h3>
          
          <label className="block">
            <div className="border-2 border-dashed border-white/20 rounded-xl p-6 sm:p-8 text-center hover:border-white/40 transition-all cursor-pointer bg-white/5">
              <FiUpload className="mx-auto text-3xl sm:text-4xl text-white/60 mb-2 sm:mb-3" />
              <p className="text-white/80 mb-1 sm:mb-2 text-sm sm:text-base">Click to upload image</p>
              <p className="text-white/40 text-xs sm:text-sm">PNG, JPG • 1920x1080px recommended</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </label>

          {preview && (
            <div className="mt-4">
              <img
                src={preview}
                alt="Hero Preview"
                className="w-full h-32 sm:h-48 object-cover bg-white/5 rounded-lg"
              />
            </div>
          )}
        </div>

        {/* Current Hero Image */}
        <div className="bg-white/5 rounded-xl p-4 sm:p-6 border border-white/10">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Current Hero Image</h3>
          
          {currentHero ? (
            <div className="space-y-4">
              <div className="bg-white/5 rounded-lg overflow-hidden h-32 sm:h-48">
                <img
                  src={currentHero}
                  alt="Current Hero"
                  className="w-full h-full object-cover"
                />
              </div>
              <button
                onClick={handleRemove}
                className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-all text-sm sm:text-base"
              >
                <FiTrash2 />
                <span>Remove Image</span>
              </button>
            </div>
          ) : (
            <div className="h-32 sm:h-48 flex items-center justify-center text-white/40 text-sm sm:text-base">
              No hero image uploaded yet
            </div>
          )}
        </div>
      </div>

      {preview && (
        <div className="mt-4 sm:mt-6 flex justify-end">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleSave}
            className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white text-slate-900 rounded-lg font-semibold hover:bg-gray-100 transition-all text-sm sm:text-base"
          >
            <FiHome />
            <span>Save Hero Image</span>
          </motion.button>
        </div>
      )}
    </div>
  );
};

export default HeroImageManager;